import { useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { useActiveConversationId } from 'hooks/useActiveConversationId'
import { useTranslation } from 'services/i18n'
import { Conversation } from 'types/Api'

import { triggerToastNotification } from 'utils/triggerToastNotification'

type UseCompletedConversationToastOptions = {
  pendingConversations: Conversation[]
}

export const useCompletedConversationToast = ({
  pendingConversations,
}: UseCompletedConversationToastOptions) => {
  const { t } = useTranslation('translation', { keyPrefix: 'conversation' })
  const navigate = useNavigate()

  const { activeConversationId } = useActiveConversationId()

  const previousPendingConversationsRef = useRef<Conversation[]>([])

  const notifyUserAboutCompletedMessage = useCallback((conversation: Conversation) => {
    triggerToastNotification({
      message: conversation.subject ? t('message_done_toast_with_subject', { subject: conversation.subject }) : t('message_done_toast'),
      variant: 'neutral',
      hasIcon: false,
      duration: Infinity,
      closeOnClick: true,
      onClose: () => {
        navigate(`/conversations/${conversation.id}`)
      },
    })
  }, [navigate, t])

  useEffect(() => {
    if (previousPendingConversationsRef.current.length) {
      previousPendingConversationsRef.current.forEach((conversation) => {
        const isPendingConversation = pendingConversations.find(pendingConversation => pendingConversation.id === conversation.id)
        const isActiveConversation = activeConversationId === conversation.id

        if (isPendingConversation || isActiveConversation) return
        notifyUserAboutCompletedMessage(conversation)
      })
    }
    previousPendingConversationsRef.current = pendingConversations
  }, [pendingConversations, activeConversationId, notifyUserAboutCompletedMessage])
}
